// Mutual-connection + shared-round client.
//
// These talk to the cross-user backend containers (connections + shared
// rounds) that live OUTSIDE the per-user state blob, so a connection or a
// shared round can't be clobbered by either user's whole-blob PUT /state.
// All calls fail soft (return false / []) — a connect that can't reach the
// network just leaves the local link in place to converge on next hydrate.

const BASE = `${process.env.REACT_APP_BACKEND_URL}/caddiegames`;

export interface ConnectedUser {
  userId: string;
  name: string;
  homeCourse?: string | null;
  handicapIndex?: number | null;
}

// Create the bidirectional A↔B link. Immediate + no approval by design.
export async function connectUser(targetUserId: string): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/connect`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetUserId }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Everyone I'm connected to (used on hydrate to derive Connected roster rows
// in both directions, AND to unlink rows whose connection has vanished).
// Returns null on failure so the caller can distinguish "no connections" from
// "couldn't fetch" — reconciliation must NOT unlink connected players just
// because a request blipped.
export async function fetchConnections(): Promise<ConnectedUser[] | null> {
  try {
    const res = await fetch(`${BASE}/connections`, { credentials: "include" });
    if (!res.ok) return null;
    const body = await res.json();
    return Array.isArray(body.connections)
      ? (body.connections as ConnectedUser[])
      : [];
  } catch {
    return null;
  }
}

// Fan a finalized round out to connected participants. Backend ignores any
// target that isn't actually connected, and is idempotent per (round,
// target) so end-round and connect-time backfill can both call it freely.
export async function shareRound(
  round: unknown,
  targetUserIds: string[],
): Promise<void> {
  if (!targetUserIds.length) return;
  try {
    await fetch(`${BASE}/rounds/share`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ round, targetUserIds }),
    });
  } catch {
    // best effort — converges on the next end-round / hydrate
  }
}

// Delete ONLY my copy of a shared round (my participant doc), so removing a
// shared round from my history doesn't resurrect on the next refresh and
// doesn't touch any other player's history. No-op on the backend if I don't
// have a shared copy (e.g. I'm the round's owner).
export async function deleteSharedRound(roundId: string): Promise<void> {
  try {
    await fetch(
      `${BASE}/rounds/shared/${encodeURIComponent(roundId)}`,
      { method: "DELETE", credentials: "include" },
    );
  } catch {
    // best effort
  }
}

// Owner-only "delete for everyone": removes the round's shared copy from
// every participant. The backend rejects this unless the caller owns the
// round. Recipients drop it on their next refresh/hydrate (reconciliation).
export async function deleteRoundForAll(roundId: string): Promise<void> {
  try {
    await fetch(
      `${BASE}/rounds/all/${encodeURIComponent(roundId)}`,
      { method: "DELETE", credentials: "include" },
    );
  } catch {
    // best effort
  }
}

// Rounds other users shared with me (I played in a round they recorded).
// Returns null on failure (network / non-OK) so callers can distinguish "no
// shared rounds" from "couldn't fetch" — reconciliation must NOT remove
// locally-cached shared rounds just because a request blipped.
export async function fetchSharedRounds(): Promise<unknown[] | null> {
  try {
    const res = await fetch(`${BASE}/rounds/shared`, { credentials: "include" });
    if (!res.ok) return null;
    const body = await res.json();
    return Array.isArray(body.rounds) ? body.rounds : [];
  } catch {
    return null;
  }
}
